import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography, Button, List, ListItem, ListItemButton, ListItemText, CircularProgress } from '@mui/material';
import { IInstitution } from '../../interfaces/institution.interface';
import { useTranslation } from 'react-i18next';
import {InstitutionService} from "../../services/institution.service";

interface InstitutionSelectionModalProps {
  open: boolean;
  institutionIds: string[];
  onSelect: (institutionId: string) => void;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const InstitutionSelectionModal: React.FC<InstitutionSelectionModalProps> = ({
  open,
  institutionIds,
  onSelect
}) => {
  const { t } = useTranslation(['institutions', 'common']);
  const [institutions, setInstitutions] = useState<IInstitution[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        setLoading(true);
        const allInstitutions = await InstitutionService.getInstitutions();

        // Filter institutions by the IDs the user has access to
        const userInstitutions = allInstitutions.filter(
          (institution: IInstitution) => institutionIds.includes(institution.id)
        );

        setInstitutions(userInstitutions);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch institutions', err);
        setError('loadFailed');
      } finally {
        setLoading(false);
      }
    };

    if (open && institutionIds.length > 0) {
      fetchInstitutions();
    }
  }, [open, institutionIds]);

  const handleInstitutionSelect = (institutionId: string) => {
    onSelect(institutionId);
  };

  return (
    <Modal
      open={open}
      aria-labelledby="institution-selection-modal"
      aria-describedby="select-your-institution"
    >
      <Box sx={modalStyle}>
        <Typography id="institution-selection-modal" variant="h6" component="h2" mb={2}>
          {t('selectionModal.title')}
        </Typography>
        <Typography id="select-your-institution" mb={2}>
          {t('selectionModal.intro')}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box>
            <Typography color="error">{t('selectionModal.loadFailed')}</Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => setLoading(true)}
            >
              {t('common:actions.retry')}
            </Button>
          </Box>
        ) : (
          <List>
            {institutions.map((institution) => (
              <ListItem disablePadding key={institution.id}>
                <ListItemButton onClick={() => handleInstitutionSelect(institution.id)}>
                  <ListItemText primary={institution.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Modal>
  );
};

export default InstitutionSelectionModal;
